import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './ModuleTwo.css'

const MODULE_ID = 2
const SECTION = 'DroneSection'   // add this line

const VIDEO_URL = 'https://upskld-demo-videos.s3.ap-south-1.amazonaws.com/module_2_types_of_drones_jobs_for_drones.mp4'

// TODO: swap these for questions that match Module 2's actual video content —
// these are placeholders following the same drone-topic thread as Module 1.
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'FVP-ன்னா என்ன?',
    options: [
      { key: 'a', label: 'Fast Pilot Vision' },
      { key: 'b', label: 'First Person View' },
      { key: 'c', label: 'Flight Path Verification' },
      { key: 'd', label: 'Fixed Payload Vehicle' },
    ],
    correctKey: 'b',
  },
  {
    id: 'q2',
    question: 'நீண்ட தூர, நீண்ட நேர military UAV-க்கு உதாரணம் எது?',
    options: [
      { key: 'a', label: 'DJI Mini' },
      { key: 'b', label: 'ZUPPA Ajeet Baza' },
      { key: 'c', label: 'IAI Heron' },
      { key: 'd', label: 'FPV racing drone' },
    ],
    correctKey: 'c',
  },
  {
    id: 'q3',
    question: 'இரவு நேரம் drone fly பண்ண எந்த factor முக்கியம்?',
    options: [
      { key: 'a', label: 'Payload மட்டும்' },
      { key: 'b', label: 'Day/Night operation capability (thermal camera)' },
      { key: 'c', label: 'Cost மட்டும்' },
      { key: 'd', label: 'Colour of drone' },
    ],
    correctKey: 'b',
  },
]

const ModuleTwo = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as Module 1.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

const sendMilestone = async (milestone) => {
  if (milestone <= lastSentMilestoneRef.current) return
  lastSentMilestoneRef.current = milestone
  await updateModuleProgress(SECTION, MODULE_ID, milestone)   // ✅ fixed
}

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so Module 3
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered
 
  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 2 complete — Module 3 unlocked!')
    navigate('/drone_course')
  }

  return (
    <div className="module_two_pg_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="module_two_pg_content">
        <h2 className="module_two_pg_title">Module 2: Core Concepts</h2>
        <p className="module_two_pg_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 3.
        </p>

        <div className="module_two_pg_video_wrap">
          <video
            className="module_two_pg_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="module_two_pg_progress_row">
          <div className="module_two_pg_progress_track">
            <div
              className="module_two_pg_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="module_two_pg_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="module_two_pg_assessment">
          <h3 className="module_two_pg_assessment_title">Module Assessment</h3>
          <p className="module_two_pg_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="module_two_pg_question_card" key={q.id}>
                <p className="module_two_pg_question_text">
                  <span className="module_two_pg_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="module_two_pg_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'module_two_pg_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'module_two_pg_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'module_two_pg_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`module_two_pg_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="module_two_pg_option_key">{opt.key.toUpperCase()}</span>
                        <span className="module_two_pg_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="module_two_pg_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="module_two_pg_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModuleTwo